import html from "nanohtml";
import type { Event } from "../../types.ts";
import { cva } from "class-variance-authority";

interface AdUnitProps {
  unitNumber: number;
  logger: (event: Event) => void;
  className?: string;
  continueText?: string;
}

const adStyles = cva("", {
  variants: {
    continueText: {
      true: "rw-mb-6 rw-mt-1",
      false: "rw-my-6",
    },
    defaultVariants: {
      continueText: false,
    },
  },
});

export function AdUnit(props: AdUnitProps): HTMLElement {
  const adId = `rw-ad-unit-${props.unitNumber}`;

  const adClasses = adStyles({
    continueText: props?.continueText ? true : false,
  });

  const adElement = html`
    ${props.continueText
      ? html`<div class="rw-mt-6 rw-text-center">${props.continueText}</div>`
      : ""}
    <div class="${adClasses} ${props.className || ""}">
      <div
        id="${adId}"
        class="voltax-recirc-ad-unit rw-min-h-[250px] rw-w-full rw-text-center rw-flex rw-items-center rw-justify-center"
        role="region"
        aria-label="Advertisement"
        data-ad-unit="${props.unitNumber}"
      ></div>
    </div>
  `;

  // Set up intersection observer for ad impression tracking
  const setupAdImpressionTracking = () => {
    const adContainer = adElement.querySelector(`#${adId}`) as HTMLElement;
    if (!adContainer) return;

    let hasLogged = false;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (
            entry.isIntersecting &&
            entry.intersectionRatio >= 0.2 &&
            !hasLogged
          ) {
            props.logger({
              name: "ad_impression",
              postOrder: props.unitNumber,
            });
            hasLogged = true;
            observer.disconnect(); // Stop observing after logging once
          }
        });
      },
      {
        threshold: 0.2, // Trigger when 20% visible
        rootMargin: "0px",
      }
    );

    observer.observe(adContainer);
  };

  // Set up tracking after a brief delay to ensure DOM is ready
  setTimeout(setupAdImpressionTracking, 100);

  return adElement;
}
